import { Link } from "react-router";
import { formStateFor } from "@/modules/booking/commands/consumerCopy";
import { reviewsCopyFor } from "@/modules/reviews/commands/reviewsCopy";
import {
  ConsumerBookings,
  type ConsumerBookingView,
} from "@/design/ConsumerBookings";
import { DataUnavailable } from "@/design/DataUnavailable";
import { PageShell } from "@/design/ui";
import { AppLink } from "~/components/AppLink";
import { ConsumerPage } from "~/components/ConsumerChrome";
import { ScreenPending } from "~/components/ScreenPending";
import { post } from "~/lib/api";
import { useQuery } from "~/lib/useQuery";

/**
 * ConsumerBookingsScreen (docs/ux/screens/ConsumerBookingsScreen.md).
 *
 * **The list is re-read after every command, and that is the only real change
 * from the server-rendered version.** There, withdrawing a request called
 * `revalidatePath` and the server sent a fresh page; here the same thing has to
 * happen explicitly, because the flags this screen renders
 * (`canCancel`, `canReview`, whether a suggestion is still live) are all
 * **derived on read** (03-core-model.md §2). A card left showing its old
 * buttons after a successful cancellation would be offering actions the server
 * will now refuse.
 *
 * Nothing is removed optimistically. These commands are irreversible, and a
 * booking that vanishes and comes back is worse than one that takes a moment.
 */
export function ConsumerBookingsScreen() {
  const bookings = useQuery<{ bookings: ConsumerBookingView[] }>("/bookings");

  /** Run a command, then re-read, so the flags on screen match the server's. */
  const after = <T,>(promise: Promise<T>, render: (value: T) => ReturnType<typeof formStateFor>) =>
    promise.then((value) => {
      bookings.reload();
      return render(value);
    });

  return (
    <ConsumerPage>
      <PageShell>
        <div className="mx-auto w-full max-w-md">
          <div className="mb-6 flex items-center justify-between">
            {/* One line, always: the header holds the title and the Inbox link
                and nothing else, so neither has to give way to the other. */}
            <h1 className="whitespace-nowrap text-xl font-bold text-ink">Your bookings</h1>
            <Link to="/inbox" className="text-sm font-medium text-accent">
              Inbox
            </Link>
          </div>

          {bookings.loading && !bookings.data ? (
            <ScreenPending />
          ) : bookings.failure ? (
            <DataUnavailable
              configured={bookings.failure !== "not_configured"}
              what="your bookings"
            />
          ) : bookings.data ? (
            <ConsumerBookings
              bookings={bookings.data.bookings}
              actions={{
                Link: AppLink,
                withdraw: (bookingId) =>
                  after(post(`/bookings/${bookingId}/withdraw`), (result) =>
                    result.ok ? {} : formStateFor(result.failure),
                  ),
                cancel: (bookingId) =>
                  after(post(`/bookings/${bookingId}/cancel`), (result) =>
                    result.ok ? {} : formStateFor(result.failure),
                  ),
                review: (bookingId, rating, body) =>
                  after(
                    post("/reviews", { bookingId, rating, body }),
                    (result) =>
                      result.ok
                        ? { success: "reviewed" }
                        : { formError: reviewsCopyFor(result.failure) },
                  ),
              }}
            />
          ) : null}
        </div>
      </PageShell>
    </ConsumerPage>
  );
}
